import {
  Timestamp,
  collection,
  firestore,
  onSnapshot,
  orderBy,
  query,
  where,
} from '@ps/functions/firebase/firestore';

import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import { firestoreDocToMessage } from './firestoreDocToMessage';

export interface MessageCallbacks {
  onNewMessage?: (message: ExtendedCommunityChannelMessage) => void;
  onMessageUpdated?: (message: ExtendedCommunityChannelMessage) => void;
  onMessageDeleted?: (messageId: string) => void;
  onError?: (error: Error) => void;
}

export interface MessageFilters {
  /**
   * Only deliver messages whose `createdAt` is strictly after this
   * timestamp. The message provider passes the timestamp of the
   * newest seeded message so the live listener starts from the next
   * arrival — matching upstream's pattern of seeding from the
   * search API and only subscribing for new rows.
   */
  startAfter?: Date;
}

export type Unsubscribe = () => void;

/**
 * Firestore live subscriber for a channel's `messages` sub-collection.
 * Web port of PopshopRN's `ChannelMessageSubscriber` —
 * `flows/community/functions/ChannelMessageMonitor.ts`. The provider
 * holds an instance per `(creatorId, channelId, parentMessageId?)`
 * tuple and calls `subscribeToChannelMessages({ startAfter }, cbs)`
 * once it has a `lastTimestamp` to anchor from.
 *
 * Construct with `parentMessageId` to scope to thread replies
 * (filters on `parentMessageId`); omit it for top-level channel
 * messages (filters on `level === 1`).
 */
export class ChannelMessageSubscriber {
  constructor(
    readonly creatorId: string,
    readonly channelId: string,
    readonly parentMessageId?: string,
  ) {}

  subscribeToChannelMessages(
    filters: MessageFilters = {},
    callbacks: MessageCallbacks = {},
  ): Unsubscribe {
    const db = firestore();
    if (!db) return () => {};

    const ref = collection(
      db,
      'stores',
      this.creatorId,
      'communityChannels',
      this.channelId,
      'messages',
    );

    const scope = this.parentMessageId
      ? where('parentMessageId', '==', this.parentMessageId)
      : where('level', '==', 1);

    const startAfter = filters.startAfter
      ? where('createdAt', '>', Timestamp.fromDate(filters.startAfter))
      : undefined;

    const q = startAfter
      ? query(ref, scope, startAfter, orderBy('createdAt', 'desc'))
      : query(ref, scope, orderBy('createdAt', 'desc'));

    return onSnapshot(
      q,
      snapshot => {
        for (const change of snapshot.docChanges()) {
          if (change.type === 'removed') {
            callbacks.onMessageDeleted?.(change.doc.id);
            continue;
          }
          const message = firestoreDocToMessage(this.channelId, change.doc);
          if (!message) continue;
          if (change.type === 'added') {
            callbacks.onNewMessage?.(message);
          } else {
            callbacks.onMessageUpdated?.(message);
          }
        }
      },
      error => callbacks.onError?.(error),
    );
  }
}
